
import React, { useState } from 'react';
import { Sparkles, ArrowRight, Save, Loader2, Award, BookOpen } from 'lucide-react';
import { RockAnalysis, Language } from '../types';
import { translations } from '../translations';
import { distillCaseRule } from '../services/knowledge_base/distiller';

interface Props {
  analysis: RockAnalysis;
  image: string;
  lang: Language;
  onClose: () => void;
  adminKey?: string;
}

export const CaseDistiller: React.FC<Props> = ({ analysis, image, lang, onClose, adminKey }) => {
  const t = translations[lang];
  const [correctName, setCorrectName] = useState('');
  const [expertReason, setExpertReason] = useState('');
  const [distilledRule, setDistilledRule] = useState<string | null>(null);
  const [isDistilling, setIsDistilling] = useState(false);

  const handleDistill = async () => {
    if (!correctName || !expertReason) return;
    setIsDistilling(true);
    try {
      const rule = await distillCaseRule(analysis, correctName, expertReason, image);
      setDistilledRule(rule);

      // Save the distilled rule to Firestore for admin review
      const saveRes = await fetch('/api/admin/save-rule', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          key: adminKey || '',
          rule: {
            rule,
            originalName: analysis.name,
            correctName: correctName,
            expertReason: expertReason,
          }
        })
      });

      if (saveRes.ok) {
        console.log("Distilled rule saved via API");
      } else {
        console.warn("Failed to save rule via API (Unauthorized or error)");
      }
    } catch (e) {
      console.error("Distillation failed:", e);
    } finally {
      setIsDistilling(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-stone-900/90 backdrop-blur-md animate-fade-in">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="bg-stone-800 p-6 text-white flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
            <Sparkles className="text-amber-400" />
            <h3 className="font-serif-sc font-bold text-xl">{t.distillTitle}</h3>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-white transition-colors">
            <Award size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 md:p-10 space-y-8">
          <div className="bg-stone-50 border border-stone-200 rounded-2xl p-6 flex flex-col md:flex-row gap-6 items-center">
            <div className="w-24 h-24 rounded-xl overflow-hidden border-2 border-stone-200 shrink-0">
              <img src={image} alt="Case" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 space-y-2 text-center md:text-left">
              <div className="text-xs font-bold text-stone-400 uppercase tracking-widest">Original Case</div>
              <div className="text-xl font-serif-sc font-bold text-stone-800">{analysis.name}</div>
            </div>
            <div className="hidden md:block text-stone-300"><ArrowRight size={32} /></div>
            <div className="flex-1 space-y-2 text-center md:text-left">
              <div className="text-xs font-bold text-amber-500 uppercase tracking-widest">Expert Correction</div>
              <input 
                type="text" 
                value={correctName}
                onChange={e => setCorrectName(e.target.value)}
                className="w-full p-2 bg-white border-b-2 border-amber-200 focus:border-amber-500 outline-none font-serif-sc font-bold text-lg"
                placeholder="Correct Name..."
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2 text-stone-500 font-bold text-xs uppercase tracking-widest">
              <BookOpen size={14} />
              <span>{t.feedbackReason}</span>
            </div>
            <textarea 
              rows={3}
              value={expertReason}
              onChange={e => setExpertReason(e.target.value)}
              className="w-full p-4 bg-stone-50 border border-stone-200 rounded-xl focus:border-stone-400 outline-none font-serif-sc text-sm leading-relaxed"
              placeholder="Why is it different? (e.g., Grain size is too small for X, looks more like Y...)"
            />
          </div>

          {distilledRule ? (
            <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-6 space-y-4 animate-fade-in">
              <div className="flex items-center gap-2 text-amber-800 font-bold text-xs uppercase tracking-widest">
                <Sparkles size={14} />
                <span>Distilled Case Rule</span>
              </div>
              <div className="p-4 bg-white border border-amber-100 rounded-xl font-serif-sc text-stone-800 italic leading-relaxed shadow-sm">
                {distilledRule}
              </div>
              <div className="text-[10px] text-amber-600/70 italic">
                * Copy this rule into core_v5_frozen.ts to upgrade the Knowledge Base.
              </div>
              <button 
                onClick={() => { navigator.clipboard.writeText(distilledRule); alert("Copied to clipboard!"); }}
                className="w-full py-3 bg-amber-600 text-white rounded-xl font-bold hover:bg-amber-700 transition-all flex items-center justify-center gap-2"
              >
                <Save size={18} />
                <span>Copy Rule</span>
              </button>
            </div>
          ) : (
            <button 
              onClick={handleDistill}
              disabled={isDistilling || !correctName || !expertReason}
              className="w-full py-5 bg-stone-800 text-amber-50 rounded-2xl font-serif-sc font-bold text-lg shadow-xl hover:bg-stone-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3"
            >
              {isDistilling ? <Loader2 className="animate-spin" /> : <Sparkles />}
              <span>{t.distillTitle}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
